/**
 * TIMEZONE DISCIPLINE — read this before formatting any date anywhere.
 *
 * Three timezones collide in this product and the collision is not
 * theoretical:
 *
 *   1. biller-tx-sync-service derives `transaction_date` — the BigQuery PARTITION KEY —
 *      in Africa/Douala.
 *   2. biller-api parses `date_from` / `date_to` as UTC
 *      (biller-api/internal/httpapi/filters.go).
 *   3. The browser, left alone, formats in whatever the user's machine says.
 *
 * Douala is UTC+1 with no DST, so a payment at 00:30 Douala on the 2nd is
 * 23:30 UTC on the 1st. Formatted in a browser set to UTC it appears in the
 * previous day — in a different partition, and in a different day's totals
 * than the ones the biller reconciles against. That is a wrong number on a
 * financial screen, not a cosmetic offset.
 *
 * So: THE APP DISPLAYS IN BUSINESS TIME, ALWAYS. Never `toLocaleString()`
 * with no timeZone, never `new Date(x).getDate()`, never `.toISOString()`
 * sliced to 10 characters. Every user-facing date goes through this module,
 * the zone comes from GET /bff/config (`businessTimezone`) rather than being
 * hardcoded in two places, and the UI labels it so the reader knows which
 * clock they are looking at (`shell.timezoneNote`).
 *
 * The mismatch at (2) is a real defect raised with the backend workstream and
 * NOT worked around here: silently shifting our filter values to compensate
 * would make the bug invisible and permanent.
 */

export interface BusinessTime {
  readonly timezone: string
  /** e.g. "21 Aug 2026, 14:05:09" */
  readonly dateTime: (value: Date | string) => string
  /** e.g. "21 Aug 2026" */
  readonly date: (value: Date | string) => string
  /** A settlement day as the API stamps it — a calendar day `YYYY-MM-DD`,
   *  not an instant — formatted like `date`. */
  readonly settlementDay: (day: string) => string
  /** Calendar day in business time, as `YYYY-MM-DD` — the form the API's
   *  date filters take. */
  readonly isoDate: (value: Date | string) => string
  readonly money: (minorOrMajor: number) => string
  readonly number: (value: number) => string
}

const toDate = (value: Date | string): Date => (value instanceof Date ? value : new Date(value))

/**
 * `en-CA` is not a locale choice, it is a formatting trick: it is the only
 * widely-available locale whose short date format is exactly ISO
 * `YYYY-MM-DD`, which lets `isoDate` derive a calendar day IN A GIVEN
 * TIMEZONE without pulling in a date library. `toISOString().slice(0,10)`
 * would give the UTC day, which is precisely the bug this module exists to
 * prevent.
 */
/**
 * Maps the app's UI language to the locale dates and money are FORMATTED with.
 *
 * These are not the same thing, and conflating them produces two real defects,
 * both of which shipped here before this function existed:
 *
 *  1. Bare `'en'` resolves to US conventions — `Aug 02, 2026`, month first.
 *     The audience is Cameroon, which writes day first in both languages, and
 *     an ambiguous `03/04/2026` on a reconciliation screen is a support call.
 *  2. Bare `'en'` with `hour12: false` renders midnight as **`24:30`**, not
 *     `00:30`, because `en-US` uses the h24 clock. A transaction timestamped
 *     `24:30` reads as corrupt data.
 *
 * Region-qualifying fixes both. `Intl` falls back to the parent language's
 * data when a region is unknown, so this cannot make formatting worse.
 */
export function formattingLocale(uiLocale: string): string {
  const base = uiLocale.split('-')[0]
  return base === 'fr' ? 'fr-CM' : 'en-CM'
}

export function createBusinessTime(timezone: string, uiLocale: string, currency: string): BusinessTime {
  const locale = formattingLocale(uiLocale)
  const dateTimeFmt = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // hourCycle, not hour12: `hour12: false` selects the h24 clock in some
    // locales, which prints midnight as 24:xx. h23 is the 00-23 clock every
    // reader expects.
    hourCycle: 'h23',
  })
  const dateFmt = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const isoFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const moneyFmt = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    // XAF has no minor unit — the CFA franc is not divided into centimes in
    // practice, and Intl already knows this. Stating it keeps the rendering
    // stable if the currency ever becomes configurable per tenant.
    maximumFractionDigits: 0,
  })
  const numberFmt = new Intl.NumberFormat(locale)

  return {
    timezone,
    dateTime: (v) => dateTimeFmt.format(toDate(v)),
    date: (v) => dateFmt.format(toDate(v)),
    // A calendar day has no instant, so one is chosen before formatting: noon
    // UTC. It is still the same calendar day in every business timezone from
    // UTC-11 to UTC+11, whereas midnight UTC — what a bare `YYYY-MM-DD`
    // parses to — is the previous evening anywhere west of Greenwich and
    // would print the day before. The zone is configuration
    // (`businessTimezone`), so the margin is not academic.
    settlementDay: (day) => dateFmt.format(new Date(`${day}T12:00:00Z`)),
    isoDate: (v) => isoFmt.format(toDate(v)),
    money: (v) => moneyFmt.format(v),
    number: (v) => numberFmt.format(v),
  }
}
